/* src/modules/dashboard/selectors.js */

import { createSelector } from 'reselect';
import { loadTexts } from '../../config/texts';

import {
  capitalize,
  filter,
  find,
  first,
  forEach,
  groupBy,
  isEmpty,
  last,
  range,
  reduce,
  upperFirst,
  zipObject,
} from 'lodash';
import moment from 'moment';

import { zeroPad, getMoment } from '../../util';


export const getLessonHelpType = createSelector(
  state => state.dashboard.lessons.data,
  state => state.appState.locale,
  (lessons, locale) => {
    const texts = loadTexts('dashboard', locale);
    const { lesson_help: {
      concept_review,
      assignment_help,
      midterm_final,
      crash_course,
      weekly_review
    } } = texts;
    const categories = [concept_review, assignment_help, midterm_final, crash_course, weekly_review];

    if (isEmpty(lessons)) return { labels: [texts.labels.no_data], data: [1] };

    const grouped = groupBy(lessons, l => l.help.type);
    let other = 0;
    const d = {};
    forEach(grouped, (v, k) => {
      if (categories.includes(k)) d[k] = v.length;
      else other += v.length;
    });
    // perserve order
    const labels = [];
    const data = [];
    forEach(d, (v, k) => {
      labels.push(capitalize(k));
      data.push(v);
    });
    if (other > 0) {
      labels.push(texts.labels.other);
      data.push(other);
    }
    if (isEmpty(labels) && isEmpty(data)) {
      labels.push(texts.labels.no_data);
      data.push(1);
    }

    return { labels, data };
  }
);

export const getPopularCourses = createSelector(
  state => state.dashboard.popularCourses.data,
  state => state.appState.locale,
  (popularCourses, locale) => {
    const texts = loadTexts('dashboard', locale);
    if (isEmpty(popularCourses)) return { labels: [texts.labels.no_data], data: [1] };
    return {
      labels: popularCourses.map(({ course }) => `${course.subject} ${course.number}`),
      data: popularCourses.map(({ completedLessons }) => completedLessons),
    };
  }
);

export const getHDYHAU = createSelector(
  state => state.dashboard.users.data,
  state => state.appState.locale,
  (users, locale) => {
    const texts = loadTexts('dashboard', locale);
    if (isEmpty(users)) return { labels: [texts.labels.no_data], data: [1] };
    const grouped = groupBy(filter(users, u => u.howDidYouHear), u => u.howDidYouHear);
    const labels = [];
    const data = [];
    forEach(grouped, (v, k) => {
      if (k !== '') {
        labels.push(upperFirst(k.split('_').join(' ')));
        data.push(v.length);
      }
    });
    if (isEmpty(labels) && isEmpty(data)) {
      labels.push(texts.labels.no_data);
      data.push(1);
    }
    return { labels, data };
  }
);

export const getLessonPriceData = createSelector(
  state => state.dashboard.lessons.data,
  state => state.appState.locale,
  (lessons, locale) => {
    const texts = loadTexts('dashboard', locale);
    const filtered = filter(lessons, l => l.status === 'COMPLETED' || l.status === 'NO_SHOW');
    if (isEmpty(filtered)) return [{ field: texts.fields.no_data, value: '' }];

    const priceWithFees = filtered.reduce((acc, x) => acc + x.price + x.studentFee, 0);
    const duration = filtered.reduce((acc, x) => acc + x.current.timeslots.length * 30, 0);
    const avePrice = priceWithFees / filtered.length;
    const aveDur = duration / filtered.length;
    return [
      {
        field: texts.fields.average_price,
        value: `$${(avePrice / 100).toFixed(2)} / ${texts.values.lesson}`
      },
      {
        field: texts.fields.average_duration,
        value: `${aveDur.toFixed(1)} Minutes / ${texts.values.lesson}`
      },
      {
        field: texts.fields.total_lesson_minutes,
        value: `${duration} ${texts.values.minutes}`
      },
    ];
  }
);

export const getCreditsData = createSelector(
  state => state.dashboard.lessons.data,
  state => state.dashboard.students.data,
  state => state.appState.locale,
  (lessons, students, locale) => {
    const texts = loadTexts('dashboard', locale);
    if (isEmpty(lessons) || isEmpty(students))
      return [{ field: texts.fields.no_data, value: '' }];

    const creditsUsed = reduce(
      lessons,
      (acc, x) => acc + reduce(x.charges, (a, y) => a + (y.creditsUsed || 0), 0),
      0
    );

    const studentCredits = reduce(students, (acc, x) => acc + x.credits, 0);
    const n = x => (x / 100).toFixed(2);
    const s = Object.keys(students).length;

    const referred = reduce(students, (acc, x) => acc + x.referred.length, 0);

    return [
      { field: texts.fields.all_students, value: `$${n(creditsUsed)} / $${n(studentCredits)}` },
      { field: texts.fields.average_student, value: `$${n(creditsUsed / s)} / $${n(studentCredits / s)}` },
      { field: texts.fields.total_referred, value: `${referred} ${texts.values.students}` },
    ];
  }
);

const getClosestStartInterval = (x, nodes) => {
  for (let i = nodes.length - 1; i >= 0; i--) {
    if (x >= nodes[i])
      return [nodes[i], i];
  }
  return [0, 0];
};

const getClosestEndInterval = (x, nodes) => {
  for (let i = 0; i < nodes.length; i++) {
    if (x < nodes[i])
      return [nodes[i], i];
  }
  return [0, 0];
};

const getDates = (start, end, time) => {
  let dates = [];
  if (time === 'day') {
    let currentDay = start.date();
    end.add(1, 'day');
    while (start.format('Y-MM') !== end.format('Y-MM')) {
      const daysInMonth = start.daysInMonth();
      const month = zeroPad(start.month() + 1);
      const year = start.year();
      dates = [...dates, ...range(currentDay, daysInMonth + 1).map(d => `${year}-${month}-${zeroPad(d)}`)];
      start.add(1, 'month');
      currentDay = 1;
    }
    const month = zeroPad(end.month() + 1);
    dates = [...dates, ...range(1, end.date() + 1).map(d => `${end.year()}-${month}-${zeroPad(d)}`)];
  } else if (time === 'week') {
    start.add(-start.day(), 'days');
    end.add(7 - end.day(), 'days');
    while (start.format('Y-MM-DD') !== end.format('Y-MM-DD')) {
      dates = [...dates, start.format('Y-MM-DD')];
      start.add(1, 'week');
    }
    dates = [...dates, end.format('Y-MM-DD')];
  } else if (time === 'month') {
    end.add(1, 'month');
    const s = moment(`${start.format('Y-MM')}-01`);
    const e = moment(`${end.format('Y-MM')}-01`);
    while (s.format('Y-MM') !== e.format('Y-MM')) {
      dates = [...dates, s.format('Y-MM-DD')];
      s.add(1, 'month');
    }
    dates = [...dates, e.format('Y-MM-DD')];
  } else if (time === 'semester') {
    const [startMonth] = getClosestStartInterval(start.month(), [0, 4, 8]);
    const [endMonth] = getClosestEndInterval(end.month(), [0, 4, 8]);
    const s = moment(`${start.year()}-${zeroPad(startMonth + 1)}-01`);
    const e = moment(`${end.year()}-${zeroPad(endMonth + 1)}-01`);
    while (s.format('Y-MM') !== e.format('Y-MM')) {
      dates = [...dates, s.format('Y-MM-DD')];
      s.add(4, 'month');
    }
    dates = [...dates, e.format('Y-MM-DD')];
  } else if (time === 'year') {
    const s = moment(`${start.year()}-01-01`);
    const e = moment(`${end.year() + 1}-01-01`);
    while (s.format('Y') !== e.format('Y')) {
      dates = [...dates, s.format('Y-MM-DD')];
      s.add(1, 'year');
    }
    dates = [...dates, e.format('Y-MM-DD')];
  }
  return dates.map(d => moment(d).valueOf());
};

export const getNumUsers = createSelector(
  state => state.dashboard.students.data,
  state => state.dashboard.tutors.data,
  state => state.dashboard.graphs.numberOfUsersTimeFrame,
  state => state.dashboard.graphs.numberOfUsersType,
  state => state.dashboard.graphs.numberOfUsersStartDate,
  state => state.dashboard.graphs.numberOfUsersEndDate,
  (students, tutors, timeFrame, type, startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const def = { data: [], labels: [] };
    if (!start.isValid() || !end.isValid() || end.valueOf() < start.valueOf()) return def;

    let d = [];
    let getTime = u => getMoment(u.createdAt.timestamp);
    switch (type) {
      case 'student':
        d = Object.values(students);
        break;
      case 'tutor':
        d = Object.values(tutors);
        break;
      case 'both':
        d = [...Object.values(students), ...Object.values(tutors)];
        break;
    }

    if (isEmpty(d)) return def;

    const dates = getDates(start, end, timeFrame);
    if (isEmpty(dates)) return def;

    const data = zipObject(dates, Array(dates.length).fill(0));

    d.forEach(f => {
      const t = getTime(f).valueOf();
      if (t >= first(dates) && t <= last(dates)) {
        // increment
        const [val] = getClosestStartInterval(t, dates);
        data[val] += 1;
      }
    });

    return {
      data: dates.map(a => data[a]),
      labels: dates.map(a => moment(a).format('MM-DD-Y')),
    };
  }
);

export const getNumLessons = createSelector(
  state => state.dashboard.lessons.data,
  state => state.dashboard.graphs.numberOfLessonsTimeFrame,
  state => state.dashboard.graphs.numberOfLessonsType,
  state => state.dashboard.graphs.numberOfLessonsStartDate,
  state => state.dashboard.graphs.numberOfLessonsEndDate,
  (lessons, timeFrame, type, startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const def = { data: [], labels: [] };
    // first filter by type
    if (!start.isValid() || !end.isValid() || end.valueOf() < start.valueOf()) return def;

    let filtered = [];
    let getTime = f => getMoment(f.current.startTime.timestamp);
    switch (type) {
      case 'completed':
        filtered = filter(lessons, l => l.status === 'COMPLETED');
        break;
      case 'cancelled':
        filtered = filter(lessons, l => l.status === 'CANCELED');
        getTime = f => getMoment(find(f.timeline, t => t.type === 'CANCEL').date.timestamp);
        break;
      case 'no_show':
        filtered = filter(lessons, l => l.status === 'NO_SHOW');
        break;
      case 'requested':
        getTime = f => getMoment(find(f.timeline, t => t.type === 'REQUEST').date.timestamp);
      default: // eslint-disable-line
        filtered = Object.values(lessons);
    }

    if (isEmpty(filtered)) return def;

    const dates = getDates(start, end, timeFrame);
    if (isEmpty(dates)) return def;

    const data = zipObject(dates, Array(dates.length).fill(0));

    forEach(filtered, f => {
      const t = getTime(f).valueOf();
      if (t >= first(dates) && t <= last(dates)) {
        // increment
        const [val] = getClosestStartInterval(t, dates);
        data[val] += 1;
      }
    });

    return {
      data: dates.map(d => data[d]),
      labels: dates.map(d => moment(d).format('MM-DD-Y')),
    };
  }
);

export const getRevenue = createSelector(
  state => state.dashboard.lessons.data,
  state => state.dashboard.graphs.revenueTimeFrame,
  state => state.dashboard.graphs.revenueType,
  state => state.dashboard.graphs.revenueStartDate,
  state => state.dashboard.graphs.revenueEndDate,
  (lessons, timeFrame, type, startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const def = { data: [], labels: [] };
    // first filter by type
    if (!start.isValid() || !end.isValid() || end.valueOf() < start.valueOf()) return def;

    let filtered = [];
    let getTime = f => getMoment(f.current.startTime.timestamp);
    let getRev = f => f.price + f.studentFee;
    switch (type) {
      case 'completed':
        filtered = filter(lessons, l => l.status === 'COMPLETED');
        break;
      case 'cancelled':
        filtered = filter(lessons, l => l.status === 'CANCELED');
        getTime = f => getMoment(find(f.timeline, t => t.type === 'CANCEL').date.timestamp);
        getRev = f => f.studentPenalty;
        break;
      case 'total':
        getRev = f => {
          if (f.status === 'CANCELED') return f.studentPenalty;
          return f.price + f.studentFee;
        };
      default: // eslint-disable-line
        filtered = Object.values(lessons);
    }

    if (isEmpty(filtered)) return def;

    const dates = getDates(start, end, timeFrame);
    if (isEmpty(dates)) return def;

    const data = zipObject(dates, Array(dates.length).fill(0));

    forEach(filtered, f => {
      const t = getTime(f).valueOf();
      if (t >= first(dates) && t <= last(dates)) {
        // increment
        const [val] = getClosestStartInterval(t, dates);
        data[val] += getRev(f);
      }
    });

    return {
      data: dates.map(d => (data[d] / 100).toFixed(2)),
      labels: dates.map(d => moment(d).format('MM-DD-Y')),
    };
  }
);

export const getProfit = createSelector(
  state => state.dashboard.lessons.data,
  state => state.dashboard.graphs.profitTimeFrame,
  state => state.dashboard.graphs.profitType,
  state => state.dashboard.graphs.profitStartDate,
  state => state.dashboard.graphs.profitEndDate,
  (lessons, timeFrame, type, startDate, endDate) => {
    const start = moment(startDate);
    const end = moment(endDate);
    const def = { data: [], labels: [] };
    // first filter by type
    if (!start.isValid() || !end.isValid() || end.valueOf() < start.valueOf()) return def;

    const filtered = filter(lessons, l => l.status === 'COMPLETED');
    const getTime = f => getMoment(f.current.startTime.timestamp);
    let getProf = () => 0;
    switch (type) {
      case 'student':
        getProf = f => f.studentFee;
        break;
      case 'tutor':
        getProf = f => f.tutorFee;
        break;
      case 'total':
      default:
        getProf = f => f.studentFee + f.tutorFee;
    }

    if (isEmpty(filtered)) return def;

    const dates = getDates(start, end, timeFrame);
    if (isEmpty(dates)) return def;

    const data = zipObject(dates, Array(dates.length).fill(0));

    forEach(filtered, f => {
      const t = getTime(f).valueOf();
      if (t >= first(dates) && t <= last(dates)) {
        // increment
        const [val] = getClosestStartInterval(t, dates);
        data[val] += getProf(f);
      }
    });

    return {
      data: dates.map(d => (data[d] / 100).toFixed(2)),
      labels: dates.map(d => moment(d).format('MM-DD-Y')),
    };
  }
);



// WEBPACK FOOTER //
// ./src/modules/dashboard/selectors.js